import * as go from 'gojs/release/go-module';

import { Layer } from 'src/containers/ProjectDiagram/constants/layers';
import {
  DRAFT_BACKGROUND_COLOR,
  DRAFT_BACKGROUND_OUTLINE_WIDTH,
  DRAFT_OUTLINE_COLOR,
  LINK_COLOR,
  LINK_LABEL_BACKGROUND_COLOR,
  LINK_LABEL_FONT_FACE,
  LINK_LABEL_FONT_SIZE,
  LINK_LABEL_FONT_WEIGHT,
  LINK_LABEL_MAX_SIZE,
  LINK_LABEL_PADDING,
} from 'src/containers/ProjectDiagram/constants/styles';
import {
  graphObjectMouseEnter,
  graphObjectMouseLeave,
} from 'src/containers/ProjectDiagram/handlers/graphObject';
import { ExtendedNode } from 'src/containers/ProjectDiagram/templates/extensions/node';
import {
  dataBinding,
  modelBinding,
  objectBinding,
} from 'src/containers/ProjectDiagram/templates/helpers/binding';
import { isMappingModified } from 'src/containers/ProjectDiagram/templates/helpers/draft';
import {
  determineColorFromSelection,
  determineValueFromDraft,
} from 'src/containers/ProjectDiagram/templates/helpers/styles';
import { MappingLabel } from 'src/containers/ProjectDiagram/types/data';
import { MappingLabelNode } from 'src/containers/ProjectDiagram/types/diagram';
import { ModelData } from 'src/containers/ProjectDiagram/types/model';

const $ = go.GraphObject.make;

export const createMappingLabelTemplate = ({
  contextMenu,
  handleClick,
  handleDoubleClick,
  toolTip,
}: {
  contextMenu: go.HTMLInfo;
  handleClick: NonNullable<MappingLabelNode['click']>;
  handleDoubleClick: NonNullable<MappingLabelNode['doubleClick']>;
  toolTip: go.HTMLInfo;
}) =>
  $(
    ExtendedNode,
    'Spot',
    {
      contextMenu,
      cursor: 'pointer',
      click: handleClick,
      doubleClick: handleDoubleClick,
      layerName: Layer.MAPPING_LABEL_BACKGROUND_LAYER,
      mouseEnter: graphObjectMouseEnter,
      mouseLeave: graphObjectMouseLeave,
      movable: false,
      selectionAdorned: false,
      toolTip,
    },

    objectBinding<go.Node, go.Node>()('layerName', 'isSelected', (isSelected) =>
      isSelected
        ? Layer.MAPPING_LABEL_FOREGROUND_LAYER
        : Layer.MAPPING_LABEL_BACKGROUND_LAYER
    ),

    $(
      go.Panel, // label container
      'Auto',
      {
        name: 'LABEL',
      },

      // white background
      $(
        go.Shape,
        'RoundedRectangle',
        {
          name: 'LABEL BACKGROUND',
          fill: LINK_LABEL_BACKGROUND_COLOR,
          stroke: LINK_LABEL_BACKGROUND_COLOR,
          strokeWidth: DRAFT_BACKGROUND_OUTLINE_WIDTH,
        },
        modelBinding<go.Shape, ModelData>()(
          'fill',
          '',
          determineValueFromDraft(
            isMappingModified,
            LINK_LABEL_BACKGROUND_COLOR,
            DRAFT_BACKGROUND_COLOR
          )
        ),
        modelBinding<go.Shape, ModelData>()(
          'stroke',
          '',
          determineValueFromDraft(
            isMappingModified,
            LINK_LABEL_BACKGROUND_COLOR,
            DRAFT_OUTLINE_COLOR
          )
        )
      ),

      $(
        go.TextBlock, // the actual label compoent
        {
          name: 'LABEL TEXT',
          stroke: LINK_COLOR,
          background: 'transparent',
          font: `${LINK_LABEL_FONT_WEIGHT} ${LINK_LABEL_FONT_SIZE}px ${LINK_LABEL_FONT_FACE}`,
          wrap: go.TextBlock.None,
          margin: new go.Margin(0, LINK_LABEL_PADDING),
          overflow: go.TextBlock.OverflowEllipsis,
        },
        dataBinding<go.TextBlock, MappingLabel>()('text'),
        modelBinding<go.TextBlock, ModelData>()(
          'stroke',
          '',
          determineColorFromSelection(isMappingModified, LINK_COLOR)
        ),
        objectBinding<go.TextBlock, go.Node>()('scale', '', (node) =>
          node.isHighlighted || node.isSelected ? 1.2 : 1
        ),
        objectBinding<go.TextBlock, go.Node>()(
          'maxSize',
          '',
          (node) =>
            new go.Size(
              node.isHighlighted || node.isSelected ? NaN : LINK_LABEL_MAX_SIZE,
              NaN
            )
        )
      )
    )
  );
